import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Eye, Building2, Hotel, PartyPopper, UtensilsCrossed, School, Car, ArrowRight, Check, Globe, Smartphone, Clock } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredTours, setFeaturedTours] = useState([]);

  useEffect(() => {
    const fetchFeaturedTours = async () => {
      try {
        const response = await axios.get(`${API}/tours?featured=true`);
        setFeaturedTours(response.data.slice(0, 3));
      } catch (error) {
        console.error('Erro ao carregar tours em destaque:', error);
      }
    };

    fetchFeaturedTours();
  }, []);

  const services = [
    {
      icon: Building2,
      title: 'Mercado Imobiliário',
      description: 'Visitas virtuais que vendem antes mesmo da chave girar. Seu imóvel aberto ao mundo, a qualquer hora.',
    },
    {
      icon: Hotel,
      title: 'Hotéis e Pousadas',
      description: 'Mostre cada canto das suas acomodações e transforme curiosidade em reservas confirmadas.',
    },
    {
      icon: PartyPopper,
      title: 'Salões de Festas',
      description: 'Seu espaço apresentado com a grandiosidade que ele merece. O cliente decide antes de visitar.',
    },
    {
      icon: UtensilsCrossed,
      title: 'Restaurantes',
      description: 'A atmosfera do seu restaurante sentida na tela. Conquiste clientes pelo ambiente, não só pelo prato.',
    },
    {
      icon: School,
      title: 'Escolas',
      description: 'Apresente a infraestrutura da sua escola para pais e alunos com sofisticação digital. Aumente a confiança e atraia novas matrículas.',
    },
    {
      icon: Car,
      title: 'Concessionárias',
      description: 'O cliente dentro do carro antes mesmo de pisar no showroom. Experiências 360° que aceleram a decisão de compra.',
    },
  ];

  const benefits = [
    { icon: Eye, text: 'Aumento de até 80% no engajamento com seu espaço' },
    { icon: Clock, text: 'Aberto para visitação 24 horas, 7 dias por semana' },
    { icon: Globe, text: 'Seu espaço acessível de qualquer lugar do planeta' },
    { icon: Smartphone, text: 'Experiência fluida em qualquer dispositivo' },
  ];

  const stats = [
    { value: '80%', label: 'Mais engajamento' },
    { value: '50%', label: 'Menos visitas improdutivas' },
    { value: '24/7', label: 'Sempre disponível' },
    { value: '100%', label: 'Imersivo e interativo' },
  ];

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Gran Tour 360",
    "image": "https://grantour360.com.br/images/og-image.jpg",
    "description": "Criamos experiências imersivas que transformam curiosidade em decisão através de Tours Virtuais 360° voltados para negócios em Curitiba e região.",
    "url": "https://grantour360.com.br",
    "telephone": "+5541999455042",
    "areaServed": {
      "@type": "City",
      "name": "Curitiba",
      "containedInPlace": {
        "@type": "State",
        "name": "Paraná"
      }
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title="Gran Tour 360 | Tours Virtuais 360° Profissionais"
        description="Gran Tour 360 - Tours Virtuais 360° profissionais para Imobiliárias, Hotéis, Restaurantes, Salões de Festas, Escolas e Concessionárias. Onde cada detalhe convida a entrar."
        url="https://grantour360.com.br"
        schema={homeSchema}
      />

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758957530781-4ff54e09bee2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMHBhbm9yYW1pYyUyMHZpZXd8ZW58MHx8fHwxNzc1MjM4MjI1fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-6" data-testid="hero-overline">
              Tours Virtuais 360°
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight" data-testid="hero-title">
              O detalhe que transforma a<br />
              visita virtual em um negócio real.
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-3xl mx-auto mb-12 leading-relaxed" data-testid="hero-description">
              Criamos experiências imersivas que transformam curiosidade em decisão. Seus espaços apresentados com a sofisticação que merecem, acessíveis de qualquer lugar, a qualquer hora.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/portfolio"
                className="px-8 py-4 bg-[#FF3366] text-white font-semibold rounded-md hover:bg-[#FF5C85] transition-all inline-flex items-center justify-center gap-2"
                data-testid="hero-cta-primary"
              >
                Explorar Portfólio
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/contato"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-md border border-white/30 hover:bg-white/20 transition-all"
                data-testid="hero-cta-secondary"
              >
                Solicitar Orçamento
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl sm:text-5xl font-bold text-[#FF3366] mb-2">{stat.value}</div>
                <p className="text-gray-500 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Marquee */}
      <section className="py-10 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-gray-400 text-xs text-center mb-6 uppercase tracking-wider font-semibold">Quem já confia na Gran Tour 360</p>
          <Marquee gradient={false} speed={40}>
            {['Imobiliária Premium', 'Grand Hotel', 'Restaurante La Maison', 'Espaço Elegance', 'Tech Innovation Hub', 'Villa Resort', 'AutoStar Motors'].map((name, i) => (
              <div key={i} className="mx-12 text-gray-300 font-bold text-xl tracking-wide">
                {name}
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Soluções</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Seu Segmento, Nossa Especialidade
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base sm:text-lg">
              Tours virtuais sob medida para negócios que entendem o poder de uma boa apresentação visual.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 p-8 rounded-lg hover:border-[#FF3366]/50 hover:shadow-lg transition-all group"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-12 h-12 bg-[#FF3366]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#FF3366]/20 transition-colors">
                    <Icon size={24} className="text-[#FF3366]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{service.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/servicos"
              className="inline-flex items-center gap-2 text-[#FF3366] font-semibold hover:gap-3 transition-all"
              data-testid="services-link"
            >
              Conhecer Todos os Serviços
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Tours */}
      <section className="py-24 bg-gray-50" data-testid="featured-tours-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Portfólio</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Veja. Sinta. Decida.
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base sm:text-lg">
              Navegue pelos nossos projetos e descubra como apresentamos o valor real de cada espaço.
            </p>
          </div>

          {featuredTours.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredTours.map((tour, index) => (
                <motion.div
                  key={tour.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
                  data-testid={`featured-tour-${index}`}
                >
                  <div className="aspect-video overflow-hidden relative">
                    <img
                      src={tour.image_url}
                      alt={`Tour virtual 360° - ${tour.title}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                      <a
                        href={tour.tour_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="opacity-0 group-hover:opacity-100 transition-all px-6 py-3 bg-[#FF3366] text-white font-semibold rounded-md inline-flex items-center gap-2"
                        data-testid={`tour-link-${index}`}
                      >
                        <Eye size={18} />
                        Ver Tour 360°
                      </a>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-[#FF3366] text-xs uppercase tracking-wider mb-2 font-semibold">{tour.category}</p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tour.title}</h3>
                    <p className="text-gray-500 text-sm">{tour.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { title: 'Residencial Premium', category: 'Imobiliário', image: 'https://images.unsplash.com/photo-1758957530781-4ff54e09bee2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMHBhbm9yYW1pYyUyMHZpZXd8ZW58MHx8fHwxNzc1MjM4MjI1fDA&ixlib=rb-4.1.0&q=85', desc: 'Apartamento de alto padrão com navegação interativa' },
                { title: 'Grand Hotel & Resort', category: 'Hotéis', image: 'https://images.pexels.com/photos/17926426/pexels-photo-17926426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', desc: 'Hotel boutique com instalações de luxo em 360°' },
                { title: 'AutoStar Showroom', category: 'Concessionárias', image: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&w=800&q=80', desc: 'Experiência imersiva dentro dos veículos' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
                  data-testid={`featured-tour-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img src={item.image} alt={`Tour virtual 360° - ${item.title}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <p className="text-[#FF3366] text-xs uppercase tracking-wider mb-2 font-semibold">{item.category}</p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF3366] text-white font-semibold rounded-md hover:bg-[#FF5C85] transition-all"
              data-testid="view-all-tours"
            >
              Ver Portfólio Completo
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Vantagens</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                O Espaço Perfeito<br />Merece Ser Explorado
              </h2>
              <p className="text-gray-500 text-base sm:text-lg mb-8 leading-relaxed">
                Seus clientes querem sentir o ambiente antes de tomar uma decisão. Nossos tours virtuais eliminam dúvidas e encurtam o caminho entre interesse e fechamento.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#FF3366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-[#FF3366]" strokeWidth={1.5} />
                      </div>
                      <span className="text-gray-700 pt-2">{benefit.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/7045941/pexels-photo-7045941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Interior de imóvel de alto padrão com tour virtual 360° interativo"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subdomain */}
      <section className="py-24 bg-[#111111]" data-testid="subdomain-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Hospedagem Exclusiva</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
            Seu Tour, Seu Endereço Digital
          </h2>
          <p className="text-white/70 text-base sm:text-lg mb-8 leading-relaxed">
            Cada cliente recebe um subdomínio exclusivo para hospedar seus tours. Seu endereço na web, sua marca fortalecida, compartilhamento simplificado.
          </p>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-8 inline-block">
            <code className="text-[#FF3366] font-mono text-lg sm:text-xl">
              seucliente.grantour360.com.br
            </code>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Pronto para Abrir as Portas<br />do Seu Espaço ao Mundo?
          </h2>
          <p className="text-gray-500 text-base sm:text-lg mb-12 leading-relaxed">
            Converse com a nossa equipe e descubra como um tour virtual 360° pode ser o diferencial que faltava para o seu negócio.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contato"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF3366] text-white font-semibold rounded-md hover:bg-[#FF5C85] transition-all"
              data-testid="final-cta"
            >
              Solicitar Orçamento Gratuito
              <ArrowRight size={20} />
            </Link>
            <a
              href="https://wa.me/5541999455042?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20os%20Tours%20Virtuais%20360%C2%B0."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-100 text-gray-900 font-semibold rounded-md hover:bg-gray-200 transition-all"
              data-testid="final-whatsapp"
            >
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
